import React, {Fragment} from "react";
import {connect} from "react-redux";
import {updatePage} from "../../actions/page";
import {
    Badge,
    Button, Col,
    ConfigProvider,
    message,
    Modal, Pagination,
    Popconfirm,
    Row,
    Space,
    Switch,
    Table,
    Tooltip
} from "antd";
import PlusOutlined from "@ant-design/icons/lib/icons/PlusOutlined";
import UndoOutlined from "@ant-design/icons/lib/icons/UndoOutlined";
import {DEFAULT_PER_PAGE} from "../../config";
import NewFirewallPolicyModal from "./NewFirewallPolicyModal";
import {bulkDeleteFirewallPolicies, deleteFirewallPolicy, loadFirewallPolicies, updateFirewallPolicyStatus} from "../../actions/firewall";
import {displayErrors, normalizeEnum} from "../../libs/utils";
import {DateTimeFormat} from "../shared/DateTimeFormat";
import {DeleteOutlined, ExclamationCircleOutlined, EyeOutlined, FolderOpenOutlined} from "@ant-design/icons";
import EditFirewallPolicyModal from "./EditFirewallPolicyModal";
import {Link} from "react-router-dom";

class FirewallPolicies extends React.Component {
    state = {
        policies: [],
        loading: false,
        pagination: { current: 1, pageSize: DEFAULT_PER_PAGE, showSizeChanger: true, total: 0 },
        showNewPolicyModal: false,
        activePolicy: '',
        showEditPolicyModal: false,
        selectedPolicies: []
    };

    componentDidMount() {
        this.props.updatePage('Firewall Policies');
        this.loadPolicies();
    }

    loadPolicies(page = null, per_page = null) {
        if(page === null) {
            page = this.state.pagination.current;
        }

        if(per_page === null) {
            per_page = this.state.pagination.pageSize;
        }

        this.setState({ loading: true });

        this.props.loadFirewallPolicies(page, per_page, (res) => {
            this.setState({ policies: res.data.data, loading: false, pagination: { current: res.data.page, pageSize: res.data.per_page, total: res.data.total, showSizeChanger: true } });
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                displayErrors(err.response.data);
                this.setState({ loading: false })
            }
        });
    }

    changePage(data) {
        this.loadPolicies(data.current, data.pageSize);
    }

    toggleFirewallPolicyStatus(guid, status) {
        let data = {
            status: status ? 'ACTIVE' : 'DISABLED'
        };

        let policies = this.state.policies.map((item) => {
            if(item.guid === guid) {
                return {...item, status: data['status']};
            }

            return item;
        });

        this.setState({ policies: policies, loading: true });

        this.props.updateFirewallPolicyStatus(guid, data, () => {
            message.success('Firewall policy successfully updated!');
            this.setState({ loading: false });
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                this.setState({ loading: false });
                displayErrors(err.response.data);
            }
        });
    }

    deleteFirewallPolicy(guid) {
        this.setState({ loading: true });

        this.props.deleteFirewallPolicy(guid, () => {
            message.success('Firewall policy successfully deleted!');
            this.loadPolicies();
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                this.setState({ loading: false });
                displayErrors(err.response.data);
            }
        });
    }

    confirmBulkDelete() {
        const { confirm } = Modal;

        confirm({
            title: 'Are you sure you want to delete these policies?',
            centered: true,
            okText: 'Yes',
            okType: 'danger',
            icon: <ExclamationCircleOutlined />,
            content: 'This can have significant impact on your domains and content!',
            onOk: () => {
                this.setState({ loading: true });

                let data = {
                    policies: this.state.selectedPolicies
                };

                this.props.bulkDeleteFirewallPolicies(data, () => {
                    message.success('Firewall policies successfully deleted!');
                    this.loadPolicies();
                }, (err) => {
                    if(typeof err.response !== 'undefined') {
                        this.setState({ loading: false });
                        displayErrors(err.response.data);
                    }
                });
            }
        })
    }

    render() {
        const columns = [
            { title: '', render: (item, record) => <Switch size='small' checked={record.status === 'ACTIVE'} onChange={(e) => this.toggleFirewallPolicyStatus(record.guid, e)} /> },
            { title: 'Name', dataIndex: 'name' },
            { title: 'Created', dataIndex: 'date_created', render: (item) => <DateTimeFormat value={item} /> },
            { title: 'Status', dataIndex: 'status', render: (item) => <Badge status={item === 'ACTIVE' ? 'success' : 'error'} text={normalizeEnum(item)} /> },
            { title: 'Owner', render: (item, record) => record.is_system_policy ? 'System' : <Link to={'/accounts/companies/' + record.owner.guid}>{record.owner.name}</Link>},
            { title: '', align: 'right', render: (item, record) => {
                    return <Space>
                        <Button size='small' icon={<EyeOutlined />} onClick={() => this.setState({ activePolicy: record.guid, showEditPolicyModal: true })}>View</Button>
                        <Popconfirm
                            title="Are you sure you want to delete this policy?"
                            onConfirm={() => this.deleteFirewallPolicy(record.guid)}
                            okText='Delete'
                            placement='topRight'
                            okButtonProps={{danger: true}}
                            cancelText='Cancel'>
                            <Button size='small' danger icon={<DeleteOutlined />}>Delete</Button>
                        </Popconfirm>
                    </Space>;
                }}
        ];

        return(
            <Fragment>
                <Row justify='space-between' style={{marginBottom: '10px'}}>
                    <Col>
                        <Space>
                            <Button size='small' type='text' icon={<PlusOutlined />} onClick={() =>this.setState({ showNewPolicyModal: true })}>Add Firewall Policy</Button>
                            <Button size='small' type='text' disabled={this.state.selectedPolicies.length === 0} icon={<DeleteOutlined />} onClick={() => this.confirmBulkDelete()}>Delete</Button>
                        </Space>
                    </Col>
                    <Col>
                        <Space>
                            <Space>
                                <Tooltip title='Reload table' placement='left'><Button onClick={() => this.loadPolicies()} icon={<UndoOutlined />} size='small' type='circle'></Button></Tooltip>
                                <Pagination onChange={(page, pageSize) => this.loadPolicies(page, pageSize)} size='small' {...this.state.pagination} />
                            </Space>
                        </Space>
                    </Col>
                </Row>

                <ConfigProvider renderEmpty={() => <div style={{ textAlign: 'center' }}>
                    <FolderOpenOutlined style={{ fontSize: 40 }} />
                    <p>Nothing to see here...</p>
                    <Button icon={<PlusOutlined />} onClick={() => this.setState({ showNewPolicyModal: true })}>Add Firewall Policy</Button>
                </div>}>

                    <Table
                        dataSource={this.state.policies}
                        rowSelection={{
                            type: 'checkbox',
                            onChange: (policies) => this.setState({ selectedPolicies: policies })
                        }}
                        columns={columns}
                        footer={() => <Row justify='space-between'>
                            <Col>{this.state.selectedPolicies.length + ' selected'}</Col>
                            <Col><Pagination onChange={(page, pageSize) => this.loadPolicies(page, pageSize)} size='small' {...this.state.pagination} /></Col>
                        </Row>}
                        rowKey={(item) => item.guid}
                        pagination={false}
                        loading={this.state.loading} />

                </ConfigProvider>

                <NewFirewallPolicyModal
                    showModal={this.state.showNewPolicyModal}
                    reloadPolicies={() => this.loadPolicies()}
                    close={() => this.setState({ showNewPolicyModal: false })} />

                <EditFirewallPolicyModal
                    showModal={this.state.showEditPolicyModal}
                    reloadPolicies={() => this.loadPolicies()}
                    policy={this.state.activePolicy}
                    close={() => this.setState({ showEditPolicyModal: false })} />
            </Fragment>
        );
    }
}

export default connect(null, { updatePage, loadFirewallPolicies, updateFirewallPolicyStatus, deleteFirewallPolicy, bulkDeleteFirewallPolicies })(FirewallPolicies);